import { useState, useLayoutEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { SEOHead, getWebPageSchema, getBreadcrumbListSchema } from '@/components/base/SEOHead';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory, articles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const [activeSlug, setActiveSlug] = useState(slug || '');

  useLayoutEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo(0, 0);
    }
  }, [slug]);

  const categoryArticles = getArticlesByCategory(activeSlug);
  const categoryName = currentCategory?.name || '分类';

  const pageTitle = `${categoryName} - 五一黑料网`;
  const pageDesc = currentCategory?.description || `${categoryName}相关内容合集`;
  const pageUrl = `https://www.51heiliao.com/category/${activeSlug}`;

  const pageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      getWebPageSchema(pageTitle, pageDesc, pageUrl),
      getBreadcrumbListSchema([
        { name: '首页', url: 'https://www.51heiliao.com/' },
        { name: categoryName, url: pageUrl },
      ]),
    ],
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`${categoryName},五一黑料网,51黑料,吃瓜,娱乐八卦`}
        canonicalUrl={pageUrl}
        schema={pageSchema}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 md:pt-24">
          {/* Breadcrumb */}
          <div className="px-4 md:px-6 py-3">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName },
              ]}
            />
          </div>

          {/* Category Header */}
          <section className="px-4 md:px-6 py-5 md:py-8" aria-label="分类标题">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-accent-500/10 text-accent-500">
                <i className={`${currentCategory?.icon || 'ri-folder-line'} text-xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {categoryName}
                </h1>
                <p className="text-sm text-foreground-500 mt-0.5">
                  共 {categoryArticles.length} 篇文章
                </p>
              </div>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="px-4 md:px-6 pb-5" aria-label="分类切换">
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-300/40'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </section>

          {/* Articles Grid */}
          <section className="px-4 md:px-6 pb-10" aria-label="文章列表">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无文章</p>
                <Link to="/" className="inline-block mt-3 text-sm text-primary-500 hover:text-primary-600">
                  返回首页
                </Link>
              </div>
            )}
          </section>

          {/* Related Categories */}
          <section className="px-4 md:px-6 pb-10" aria-label="相关分类">
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-4">
              探索更多分类
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
              {categories
                .filter((c) => c.slug !== activeSlug)
                .slice(0, 6)
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-3 p-3 rounded-lg bg-background-100 border border-background-300/30 hover:border-accent-500/30 transition-colors cursor-pointer"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500/10 text-accent-500 flex-shrink-0">
                      <i className={`${cat.icon} text-sm`}></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">
                      {cat.name}
                    </span>
                  </Link>
                ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}